<template>
  <Container>
    <template v-slot:header>Job Management</template>
    <template v-slot:content>
      <JobsOrgSummary
        :params="summaryParams"
        :selected-org="filters.organization"
        @select="onOrgSelected"
        @loaded="onOrgsLoaded"
      />

      <div class="tab bg-white shadow rounded-t-md p-8 pt-4 hidden-print">
        <nav class="float-left">
          <a
            v-for="tab in tabs"
            :key="tab.name"
            :class="[
              tab.current
                ? 'current'
                : 'border-transparent hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap mr-5 pb-2 px-1 border-b-2 text-md cursor-pointer',
            ]"
            @click="changeTab(tab.name)"
          >
            {{ tab.name }}
          </a>
        </nav>
      </div>

      <div v-if="activeTab === 'All Jobs'" class="flex flex-row-reverse mt-6">
        <SearchInput
          placeholder="Search by name, email, file name, or job ID"
          v-model="searchKeyword"
        />
      </div>

      <section
        v-if="activeTab === 'All Jobs'"
        class="filter-grid grid grid-cols-1 gap-x-4 gap-y-3 mt-6 text-left sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6"
      >
        <label class="flex flex-col text-left text-gray-600">
          <span class="mb-1 text-sm">Action</span>
          <select v-model="filters.action" class="form-field-no-icon">
            <option value="">Any</option>
            <option
              v-for="opt in actionOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </label>

        <label class="flex flex-col text-left text-gray-600">
          <span class="mb-1 text-sm">Created With</span>
          <select v-model="filters.created_with" class="form-field-no-icon">
            <option value="">Any</option>
            <option v-for="opt in createdWithOptions" :key="opt" :value="opt">
              {{ opt }}
            </option>
          </select>
        </label>

        <label class="flex flex-col text-left text-gray-600">
          <span class="mb-1 text-sm">Status</span>
          <select v-model="filters.status" class="form-field-no-icon">
            <option :value="''">Any</option>
            <option
              v-for="opt in statusOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </label>

        <label class="flex flex-col text-left text-gray-600">
          <span class="mb-1 text-sm">Account</span>
          <select v-model="filters.organization" class="form-field-no-icon">
            <option :value="''">Any</option>
            <option v-for="org in orgOptions" :key="org.id" :value="org.id">
              {{ org.label }}
            </option>
          </select>
        </label>

        <label class="flex flex-col text-left text-gray-600">
          <span class="mb-1 text-sm">Start Date</span>
          <input
            type="date"
            class="focus:outline-none placeholder-gray-400 sm:text-sm date_inp"
            v-model="filters.start_date"
          />
        </label>

        <label class="flex flex-col text-left text-gray-600">
          <span class="mb-1 text-sm">End Date</span>
          <input
            type="date"
            class="focus:outline-none placeholder-gray-400 sm:text-sm date_inp"
            v-model="filters.end_date"
          />
        </label>
      </section>

      <div class="w-full mt-6 overflow-x-auto">
        <div class="align-middle bg-white inline-block min-w-full">
          <table class="divide-gray-300 min-w-full text-left table-auto">
            <thead class="border-b-2">
              <tr>
                <th
                  v-for="col in columns"
                  :key="col.name"
                  scope="col"
                  class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <FilterableHeaderCell
                    v-if="col.param && activeTab === 'All Jobs'"
                    :title="col.name"
                    :param="col.param"
                    @switchOrder="onSwitchOrder"
                  />
                  <span v-else>{{ col.name }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-if="isLoading">
                <tr>
                  <td :colspan="columns.length" class="text-center py-10">
                    <PulseLoader color="#ff5a5f" />
                  </td>
                </tr>
              </template>
              <template v-else-if="jobs.length">
                <tr v-for="job in jobs" :key="job.id">
                  <td class="px-6 py-5 text-sm text-gray-500">#{{ job.id }}</td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    <div class="truncate max-w-xs" :title="orgLabel(job)">
                      {{ orgLabel(job) }}
                    </div>
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    <div
                      class="truncate max-w-xs"
                      :title="job.input_file?.name"
                    >
                      {{ job.input_file?.name || "—" }}
                    </div>
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    <router-link
                      v-if="job.created_by_id"
                      :to="{
                        name: 'UserDetails',
                        params: { userId: job.created_by_id },
                      }"
                      class="text-blue-600 hover:underline"
                    >
                      {{ job.created_by_name || job.created_by }}
                    </router-link>
                    <span v-else>{{ job.created_by || "—" }}</span>
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ actionLabel(job.actions) }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500 whitespace-nowrap">
                    {{ formatN(job.emails_count) }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ formattedDate(job.created_at) }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500 whitespace-nowrap">
                    {{ job.duration || "—" }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    <span class="inline-flex flex-wrap items-center">
                      <span
                        :class="`
                          px-2
                          text-${statusData(job).color}-800
                          bg-${statusData(job).color}-100
                          rounded-full
                        `"
                      >
                        {{ statusData(job).displayName }}
                      </span>
                      <span
                        v-if="isStuck(job)"
                        class="ml-1 px-2 text-red-800 bg-red-100 rounded-full whitespace-nowrap"
                        :title="`Stuck for ${job.duration}`"
                      >
                        &#9888; stuck
                      </span>
                      <span
                        v-if="hasAuditTrail(job)"
                        class="my-auto ml-1 text-left has-tooltip"
                      >
                        <QuestionMarkCircleIcon class="h-4" />
                        <Tooltip>{{ auditTooltip(job) }}</Tooltip>
                      </span>
                    </span>
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ job.admin_retry_count || "—" }}
                  </td>
                  <td class="px-6 py-5">
                    <div class="flex space-x-2">
                      <button
                        type="button"
                        class="button-submit w-24"
                        :disabled="!canRetry(job)"
                        @click="retryJob(job)"
                      >
                        Retry
                      </button>
                      <button
                        type="button"
                        class="button-cancel w-24"
                        :disabled="!canCancel(job)"
                        @click="cancelJob(job)"
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr>
                  <td
                    :colspan="columns.length"
                    class="px-6 py-5 text-sm text-gray-500 text-left"
                  >
                    No data
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <Paginator
            ref="paginator"
            :data="pagination"
            :page-size="15"
            :onPageChange="onPageChange"
          />
        </div>
      </div>
    </template>
  </Container>
</template>

<script>
import Container from "@/components/common/Container";
import Paginator from "@/components/common/Paginator";
import FilterableHeaderCell from "@/components/common/FilterableHeaderCell";
import SearchInput from "@/components/common/SearchInput";
import JobsOrgSummary from "@/components/admin/jobs-page/JobsOrgSummary";
import Tooltip from "@/components/basic/common/Tooltip";
import PulseLoader from "vue-spinner/src/PulseLoader";
import { QuestionMarkCircleIcon } from "@heroicons/vue/solid";
import humanize from "humanize";
import { ref, reactive } from "vue";
import useConfirmationModal from "@/api/confirmationModal";
import eventBus from "@/utils/eventBus";

// Mirrors JobsAdminViewSet.STUCK_CREATED_THRESHOLD / STUCK_PROCESSING_THRESHOLD
// in backend/ak/api/admin/views.py, which is what both the retry endpoint and
// GET /admin/jobs/stuck gate on. The Stuck Jobs tab asks that endpoint, but a
// row in the All Jobs list carries no stuck flag, so the badge and the retry
// button derive it here from fields every row already has.
const STUCK_CREATED_MS = 30 * 60 * 1000;
const STUCK_PROCESSING_MS = 2 * 60 * 60 * 1000;
export default {
  name: "JobManagement",
  components: {
    Container,
    Paginator,
    FilterableHeaderCell,
    SearchInput,
    JobsOrgSummary,
    Tooltip,
    PulseLoader,
    QuestionMarkCircleIcon,
  },
  data() {
    return {
      jobs: ref([]),
      pagination: ref({}),
      isLoading: false,
      page: 1,
      ordering: "-created_at",
      searchKeyword: "",
      filters: reactive({
        action: "",
        created_with: "",
        status: "",
        organization: "",
        start_date: "",
        end_date: "",
      }),
      // Populated from the org-summary rollup -- it is the only endpoint that
      // lists organizations, and accounts/users is paginated 15 to a page.
      orgOptions: [],
      tabs: [
        { name: "All Jobs", current: true },
        { name: "Stuck Jobs", current: false },
      ],
      activeTab: "All Jobs",
      columns: [
        { name: "Job ID", param: "id" },
        // Organizations have no name, so there is nothing to sort this by.
        { name: "Account", param: null },
        { name: "File", param: "input_file__name" },
        { name: "Creator", param: "created_by__email" },
        { name: "Service", param: "actions" },
        { name: "Emails", param: "emails_count" },
        { name: "Created At", param: "created_at" },
        { name: "Duration", param: "duration" },
        { name: "Status", param: "status" },
        { name: "Retries", param: null },
        { name: "Actions", param: null },
      ],
      actionOptions: [
        { value: "evaluation", label: "Evaluation" },
        { value: "threat_evaluation", label: "Threat Evaluation" },
        { value: "verification", label: "Deliverability + Threat Detection" },
        { value: "deliverability", label: "Deliverability Check" },
        { value: "threat_assessment", label: "Threat Detection" },
      ],
      createdWithOptions: ["UI", "API"],
      statusOptions: [
        { value: "CREATED", label: "Created" },
        { value: "PROCESSING", label: "Processing" },
        { value: "COMPLETED", label: "Completed" },
        { value: "ERROR", label: "Error" },
        { value: "CANCELLED", label: "Cancelled" },
        { value: "AUTO_DELETED", label: "Auto-Deleted" },
      ],
      serviceMappings: {
        evaluation: "Evaluation",
        threat_evaluation: "Threat Evaluation",
        verification: "Deliverability + Threat Detection",
        deliverability: "Deliverability Check",
        threat_assessment: "Threat Detection",
        real_time: "Real Time",
      },
    };
  },
  computed: {
    // Deliberately excludes `organization` and `search`: the rollup is the org
    // filter's option source, so narrowing it to the selected org would leave
    // the dropdown with a single entry and no way back.
    summaryParams() {
      return {
        action: this.filters.action,
        created_with: this.filters.created_with,
        status: this.filters.status,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
      };
    },
  },
  watch: {
    // SearchInput sets inheritAttrs: false, so an @input listener on it is
    // silently dropped -- watch the debounced model it emits instead.
    searchKeyword() {
      this.resetAndFetch();
    },
    "filters.action"() {
      this.resetAndFetch();
    },
    "filters.created_with"() {
      this.resetAndFetch();
    },
    "filters.status"() {
      this.resetAndFetch();
    },
    "filters.organization"() {
      this.resetAndFetch();
    },
    "filters.start_date"() {
      this.resetAndFetch();
    },
    "filters.end_date"() {
      this.resetAndFetch();
    },
  },
  mounted() {
    this.fetchJobs();
  },
  methods: {
    changeTab(tabName) {
      this.tabs.forEach((tab) => {
        tab.current = tab.name === tabName;
      });
      this.activeTab = tabName;
      this.resetAndFetch();
    },
    resetAndFetch() {
      this.page = 1;
      if (this.$refs.paginator) {
        // setPage() is the exposed method every other consumer uses;
        // assigning to .page leaves the Paginator's own state untouched and
        // never fires onPageChange. setPage() calls it, so it fetches.
        this.$refs.paginator.setPage(1);
      } else {
        this.fetchJobs();
      }
    },
    onSwitchOrder(ordering) {
      this.ordering = !ordering ? "-created_at" : ordering;
      this.resetAndFetch();
    },
    onSearchInput() {
      this.resetAndFetch();
    },
    async onPageChange(page) {
      this.page = page;
      await this.fetchJobs();
    },
    async fetchJobs() {
      if (this.activeTab === "Stuck Jobs") {
        await this.fetchStuckJobs();
      } else {
        await this.fetchAllJobs();
      }
    },
    async fetchAllJobs() {
      this.isLoading = true;
      const urlParams = {
        ordering: this.ordering,
        page: this.page,
        size: 15,
        search: this.searchKeyword,
        action: this.filters.action,
        created_with: this.filters.created_with,
        status: this.filters.status,
        organization: this.filters.organization,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
      };
      const response = await this.$store.dispatch("fetching/FETCH", {
        apiCall: () => this.$alfredService.fetchAdminJobs(urlParams),
      });
      if (response) {
        const { results, ...paginationData } = response;
        this.jobs = results;
        this.pagination = paginationData;
      }
      this.isLoading = false;
    },
    async fetchStuckJobs() {
      this.isLoading = true;
      const response = await this.$store.dispatch("fetching/FETCH", {
        apiCall: () =>
          this.$alfredService.fetchStuckAdminJobs({
            page: this.page,
            size: 15,
          }),
      });
      if (response) {
        const { results, ...paginationData } = response;
        this.jobs = results;
        this.pagination = paginationData;
      }
      this.isLoading = false;
    },
    formatN(count) {
      // emails_count is nullable on both Job and Upload; a NULL is "unknown",
      // not zero, so don't render it as a real count.
      if (count === null || count === undefined) {
        return "—";
      }
      return humanize.numberFormat(count, 0);
    },
    isStuck(job) {
      const now = Date.now();
      if (job.status === "CREATED") {
        return now - new Date(job.created_at).getTime() >= STUCK_CREATED_MS;
      }
      if (job.status === "PROCESSING") {
        return now - new Date(job.modified_at).getTime() >= STUCK_PROCESSING_MS;
      }
      return false;
    },
    orgLabel(job) {
      return job.organization_id ? `Org #${job.organization_id}` : "—";
    },
    onOrgSelected(orgId) {
      this.filters.organization = orgId;
    },
    onOrgsLoaded(orgs) {
      this.orgOptions = orgs;
    },
    actionLabel(actionsList) {
      if (!actionsList?.length) {
        return "—";
      }
      return actionsList
        .map((action) => this.serviceMappings[action] || action)
        .join(", ");
    },
    formattedDate(value) {
      if (!value) {
        return "—";
      }
      return new Date(value).toLocaleString("en-US");
    },
    statusData(job) {
      // Jobs cancelled before the CANCELLED status shipped are still stored
      // as ERROR with cancelled_at set, so keep rendering those as Cancelled.
      if (job.status === "ERROR" && job.cancelled_at) {
        return { displayName: "Cancelled", color: "yellow" };
      }
      const map = {
        CREATED: { displayName: "Created", color: "gray" },
        PROCESSING: { displayName: "Processing", color: "blue" },
        COMPLETED: { displayName: "Completed", color: "green" },
        ERROR: { displayName: "Error", color: "red" },
        CANCELLED: { displayName: "Cancelled", color: "yellow" },
        AUTO_DELETED: { displayName: "Auto-Deleted", color: "gray" },
      };
      return map[job.status] || { displayName: job.status, color: "gray" };
    },
    hasAuditTrail(job) {
      return !!(job.cancelled_at || job.retried_by || job.admin_retry_count);
    },
    auditTooltip(job) {
      const lines = [];
      if (job.admin_retry_count) {
        lines.push(`Retried ${job.admin_retry_count} time(s)`);
      }
      if (job.admin_last_retry_at) {
        lines.push(
          `Last retried: ${this.formattedDate(job.admin_last_retry_at)}`
        );
      }
      if (job.retried_by) {
        lines.push(`Retried by: ${job.retried_by}`);
      }
      if (job.cancelled_at) {
        lines.push(`Cancelled: ${this.formattedDate(job.cancelled_at)}`);
      }
      if (job.cancelled_by) {
        lines.push(`Cancelled by: ${job.cancelled_by}`);
      }
      return lines.join("\n");
    },
    canRetry(job) {
      if (
        ["COMPLETED", "CANCELLED", "AUTO_DELETED"].includes(job.status) ||
        job.cancelled_at
      ) {
        return false;
      }
      // Same helper the badge uses, so what looks stuck is exactly what the
      // retry endpoint will accept.
      if (["CREATED", "PROCESSING"].includes(job.status)) {
        return this.isStuck(job);
      }
      return true;
    },
    canCancel(job) {
      return !["COMPLETED", "ERROR", "CANCELLED"].includes(job.status);
    },
    async retryJob(job) {
      const modal = useConfirmationModal();
      await modal.openModal({
        title: `Retry job for ${job.created_by}?`,
        onSubmit: async () => {
          try {
            const response = await this.$alfredService.retryAdminJob(job.id);
            eventBus.trigger("notify", {
              notification: {
                group: "top-right",
                type: "success",
                text: response.message,
              },
            });
            await this.fetchJobs();
          } catch (error) {
            eventBus.trigger("notify", {
              notification: {
                group: "top-right",
                type: "error",
                text: error?.response?.data?.error || "Failed to retry job.",
              },
            });
          }
        },
      });
    },
    async cancelJob(job) {
      const modal = useConfirmationModal();
      await modal.openModal({
        title: `Cancel job for ${job.created_by}?`,
        onSubmit: async () => {
          try {
            const response = await this.$alfredService.cancelAdminJob(job.id);
            eventBus.trigger("notify", {
              notification: {
                group: "top-right",
                type: "success",
                text: response.message,
              },
            });
            await this.fetchJobs();
          } catch (error) {
            eventBus.trigger("notify", {
              notification: {
                group: "top-right",
                type: "error",
                text: error?.response?.data?.error || "Failed to cancel job.",
              },
            });
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.current {
  border-color: var(--main-color);
  color: var(--main-color);
}
select {
  background-image: none;
}
/*
 * Every filter control fills its grid cell so the row stays aligned.
 * !important is required: tailwind.config.js sets `important: true`, so the
 * `sm:w-8/12` that `form-field-no-icon` applies lands as `!important` too.
 */
.filter-grid select,
.filter-grid input {
  width: 100% !important;
  min-width: 0;
  height: 2.375rem;
}
.date_inp {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  border-width: 1px;
  border-radius: 0.375rem;
  --tw-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000),
    var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
}
</style>
